import type { ErrorEvent, EventHint } from '@sentry/angular'

import { ApiError } from '../api/utils/fetch-utils'

/**
 * Sentry `beforeSend`: tags an event raised by an `ApiError` with what joins it to the backend, and
 * drops the ones nobody can act on.
 *
 * Dropped, whoever raised them:
 * - a network failure (no response): a visitor on bad mobile data. Lowering it to a warning still
 *   opened an issue, used quota and attached a replay for every dropped request a fire-and-forget
 *   call let escape - SEIRI-WEB-3W, -42 and -43 were all this. An outage shows up in the backend's
 *   own monitoring, not here.
 * - a 401 with no code (`ApiError.isUnauthenticated`): the session expired, and `HANDLE_AUTH_ERROR`
 *   has already signed the user out (SEIRI-WEB-41).
 *
 * A coded refusal (`ApiError.isRefusal` - already registered, event full) that nothing caught is
 * lowered to a warning instead. It is the API working as designed; what is worth seeing is that no
 * screen handled it, so the user was never told why.
 *
 * It has to run here rather than where the error is logged, because most failed requests never
 * pass through `LoggerService.error` - they escape to Angular's ErrorHandler or surface as an
 * unhandled rejection, and Sentry captures them on its own. Sentry also does not serialise an
 * Error's own fields, so without this the `reqId` an `ApiError` carries never reaches the event.
 *
 * Tags, not extras: only tags are indexed and searchable. `reqId` is the join key to the backend
 * log lines (`reqId:<id>` in the log store); status and code make the issue list filterable.
 */
export function tagApiError(event: ErrorEvent, hint: EventHint): ErrorEvent | null {
  const error = hint.originalException
  if (!(error instanceof ApiError)) return event
  if (error.isNetworkFailure || error.isUnauthenticated) return null

  event.tags = {
    ...event.tags,
    ...(error.reqId ? { reqId: error.reqId } : {}),
    'api.status': error.statusCode,
    'api.code': error.code,
  }
  if (error.isRefusal) event.level = 'warning'
  return event
}
